import React from 'react';
import { Box, Card, CardContent, Typography, Tabs, Tab, Switch, FormControlLabel, Collapse, IconButton, Autocomplete, TextField } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { SAP_TYPOGRAPHY } from '../theme/sapDesignSystem';
// no API calls in this component; tab panels use dedicated components
import StepSettings from './StepSettings';
import StepRequestValidator from './StepRequestValidator';
import { SAPTextField } from '../components/design-system/SAPTextField';
import { SAPButton } from '../components/design-system/SAPButton';

export interface StepDetailTabsProps<StepModel> {
	selectedStep: StepModel | null;
	editValues: Partial<StepModel>;
	adaptersList: string[]; // Step Type options (enums[2])
	typeOptions: string[]; // Type options (enums[2])
	objectTypeOptions: string[]; // Object Type options (enums[0])
	resultOptions: string[]; // Result options (enums[1])
	responseTypeOptions: string[];
	jobDetails: Array<any[]>; // Job details for Failure Job Detail Id dropdown
	steps: Array<{ id: number; stepCode: string | null; sequence: number }>; // Steps for Parent Id dropdown
	updateEditField: (key: keyof StepModel, value: any) => void;
	isDirty: boolean;
	validationErrors: Record<string, string>;
	revertChanges: () => void;
	saveSelectedStep: () => void;
	firstFieldRef: React.RefObject<HTMLInputElement>;
	/** When true, hide Save and disable all fields (permission STEP_EDIT for step detail/settings/request validator). */
	readOnly?: boolean;
}

// Helper function to convert number to words (step_one, step_two, etc.)
const numberToStepWord = (num: number): string => {
	const words = ['zero', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine', 'ten',
		'eleven', 'twelve', 'thirteen', 'fourteen', 'fifteen', 'sixteen', 'seventeen', 'eighteen', 'nineteen', 'twenty'];
	if (num >= 0 && num <= 20) {
		return `step_${words[num]}`;
	}
	return `step_${num}`;
};

function StepDetailTabs<StepModel extends Record<string, any>>(props: StepDetailTabsProps<StepModel>) {
	const {
		selectedStep,
		editValues,
		adaptersList,
		typeOptions,
		objectTypeOptions,
		resultOptions,
		responseTypeOptions,
		jobDetails,
		steps,
		updateEditField,
		isDirty,
		validationErrors,
		revertChanges,
		saveSelectedStep,
		firstFieldRef,
		readOnly = false,
	} = props;

const [tab, setTab] = React.useState(0);

// Collapsible editors open state with persistence
const storageKey = 'stepDetailEditorsOpenState';
const [openEditors, setOpenEditors] = React.useState<{ header: boolean; request: boolean; responseFmt: boolean; requestFmt: boolean; errorFmt: boolean }>(() => {
    try {
        const raw = localStorage.getItem(storageKey);
        if (raw) return JSON.parse(raw);
    } catch {}
    return { header: false, request: true, responseFmt: false, requestFmt: false, errorFmt: false };
});
React.useEffect(() => {
    try {
        localStorage.setItem(storageKey, JSON.stringify(openEditors));
    } catch {}
}, [openEditors]);

// Keyboard save shortcut (disabled when read-only)
React.useEffect(() => {
    if (readOnly) return;
    const onKey = (e: KeyboardEvent) => {
        const isSave = (e.ctrlKey || e.metaKey) && (e.key === 's' || e.key === 'S');
        if (isSave) {
            e.preventDefault();
            if (tab === 0) {
                saveSelectedStep();
            }
        }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
}, [tab, saveSelectedStep, readOnly]);

// Step settings and request validator content are now rendered via dedicated
// components: <StepSettings /> and <StepRequestValidator />

	return (
		<Card sx={{ borderRadius: 3, overflow: 'hidden' }}>
			<CardContent sx={{ p: { xs: 1.5, md: 2 },
			  // Remove blue hover color locally for this page
			  '& .MuiTab-root:hover': { color: 'text.primary' },
			  '& .MuiFormLabel-root:hover': { color: 'text.secondary' },
			}}>
				<Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 1 }}>
					<Tab label="Step detail" />
					<Tab label="Step settings" />
					<Tab label="Step request validator" />
				</Tabs>
				{/* Visual separation between tabs and content */}
				<Box sx={{ borderBottom: '1px solid', borderColor: 'divider', mb: 1 }} />

                {tab === 0 && (
                    <>
                        {/* Sticky actions header */}
                        <Box sx={{
                            position: 'sticky',
                            top: 0,
                            zIndex: 1,
                            backgroundColor: 'background.paper',
                            pb: 1,
                            mb: 1.5,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            borderBottom: '1px solid',
                            borderColor: 'divider',
                        }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                {/* Removed redundant "Step detail" heading since it's already shown in tabs */}
                            </Box>
                            {!readOnly && (
                                <Box sx={{ display: 'flex', gap: 1 }}>
                                    <SAPButton 
                                        variant="primary" 
                                        size="sm" 
                                        icon="save" 
                                        onClick={saveSelectedStep} 
                                        disabled={Object.keys(validationErrors).length > 0 || !isDirty}
                                    >
                                        Save
                                    </SAPButton>
                                </Box>
                            )}
                        </Box>
                        {/* Validation summary */}
                        {!readOnly && Object.keys(validationErrors).length > 0 && (
                            <Box role="alert" sx={{
                                mb: 2,
                                p: 1.25,
                                borderRadius: 1,
                                border: '1px solid',
                                borderColor: 'error.light',
                                backgroundColor: 'error.lighter',
                                color: 'error.main',
                                fontSize: 13,
                            }}>
                                ⚠ Please fix the highlighted fields: {Object.values(validationErrors).join(', ')}
                            </Box>
                        )}
                        {selectedStep ? (
                            <Box component="fieldset" disabled={readOnly} sx={{ border: 'none', p: 0, m: 0, minWidth: 0 }}>
                            <Box sx={{ 
                                display: 'grid', 
                                gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' }, // Two-column split as per design spec
                                columnGap: { xs: 1.5, lg: 3 }, // 20px gutters as per design spec
                                rowGap: { xs: 1.5, lg: 2 }, 
                                alignItems: 'start', 
                                '& .MuiTextField-root': { width: '100%' },
                                '& .MuiFormControlLabel-root': { 
                                    width: '100%',
                                    margin: 0,
                                    padding: { xs: '8px 0', sm: '4px 0' }
                                }
                            }}>
                                {/* LEFT COLUMN - Basics Section */}
                                <Box sx={{ gridColumn: '1 / 2' }}>
                                    <Typography variant="h6" sx={{ 
                                        fontSize: 16, // 16px section headers as per design spec
                                        fontWeight: SAP_TYPOGRAPHY.fontWeight.semibold,
                                        mb: 2,
                                        color: 'text.primary'
                                    }}>
                                        Basics
                                    </Typography>
                                    
                                    {/* 3 rows × 2 columns grid layout */}
                                    <Box sx={{ 
                                        display: 'grid', 
                                        gridTemplateColumns: '1fr 1fr', 
                                        gridTemplateRows: 'repeat(3, auto)', 
                                        gap: 2,
                                        '& .MuiTextField-root': { width: '100%' }
                                    }}>
                                        {/* Row 1 */}
                                        <Box>
                                            <Typography variant="body2" sx={{ mb: 0.5, fontSize: 12 }}>Step Code</Typography>
                                            <SAPTextField 
                                                value={(editValues as any).stepCode || ''} 
                                                onChange={(e) => updateEditField('stepCode' as any, e.target.value)} 
                                                size="sm" 
                                                fullWidth 
                                                placeholder="e.g. chec_all_step (optional)"
                                                inputRef={firstFieldRef}
                                            />
                                        </Box>
                                        
                                        <Box>
                                            <Typography variant="body2" sx={{ mb: 0.5, fontSize: 12 }}>Step Type</Typography>
                                            <Autocomplete
                                                options={adaptersList}
                                                value={(editValues as any).adapter || (editValues as any).stepType || null}
                                                onChange={(_, newValue) => updateEditField('adapter' as any, (typeof newValue === 'string' ? newValue : newValue ?? '') || '')}
                                                freeSolo
                                                size="small"
                                                renderInput={(params) => (
                                                    <TextField
                                                        {...params}
                                                        placeholder="Select or type..."
                                                        error={!!(validationErrors as any).adapter}
                                                        helperText={(validationErrors as any).adapter ? (validationErrors as any).adapter : ' '}
                                                        sx={{
                                                            '& .MuiOutlinedInput-root': { fontSize: '0.875rem', height: '32px' },
                                                        }}
                                                    />
                                                )}
                                                sx={{ width: '100%' }}
                                            />
                                        </Box>
                                        
                                        {/* Row 2 */}
                                        <Box>
                                            <Typography variant="body2" sx={{ mb: 0.5, fontSize: 12 }}>Failure Job Detail Id</Typography>
                                            <Autocomplete
                                                options={jobDetails.map((job) => ({
                                                    id: job[0],
                                                    label: job[2] || `Job ${job[0]}`,
                                                }))}
                                                value={jobDetails.find((job) => job[0] === (editValues as any).failureJobDetailId) ? {
                                                    id: (editValues as any).failureJobDetailId,
                                                    label: jobDetails.find((job) => job[0] === (editValues as any).failureJobDetailId)?.[2] || `Job ${(editValues as any).failureJobDetailId}`,
                                                } : null}
                                                onChange={(_, newValue) => {
                                                    updateEditField('failureJobDetailId' as any, newValue?.id || null);
                                                }}
                                                getOptionLabel={(option) => option.label}
                                                isOptionEqualToValue={(option, value) => option.id === value.id}
                                                size="small"
                                                renderInput={(params) => (
                                                    <TextField
                                                        {...params}
                                                        placeholder="Search job..."
                                                        sx={{
                                                            '& .MuiOutlinedInput-root': {
                                                                fontSize: '0.875rem',
                                                                height: '32px',
                                                            },
                                                        }}
                                                    />
                                                )}
                                                sx={{ width: '100%' }}
                                            />
                                        </Box>
                                        
                                        <Box>
                                            <Typography variant="body2" sx={{ mb: 0.5, fontSize: 12 }}>Type</Typography>
                                            <Autocomplete
                                                options={typeOptions}
                                                value={(editValues as any).type || null}
                                                onChange={(_, newValue) => updateEditField('type' as any, (typeof newValue === 'string' ? newValue : newValue ?? '') || '')}
                                                freeSolo
                                                size="small"
                                                renderInput={(params) => (
                                                    <TextField
                                                        {...params}
                                                        placeholder="Select or type..."
                                                        sx={{
                                                            '& .MuiOutlinedInput-root': { fontSize: '0.875rem', height: '32px' },
                                                        }}
                                                    />
                                                )}
                                                sx={{ width: '100%' }}
                                            />
                                        </Box>
                                        
                                        {/* Row 3 */}
                                        <Box>
                                            <Typography variant="body2" sx={{ mb: 0.5, fontSize: 12 }}>Parent Id</Typography>
                                            <Autocomplete
                                                options={steps
                                                    .filter((s) => s.id !== (selectedStep as any)?.id)
                                                    .map((step) => ({
                                                        id: step.id,
                                                        label: step.stepCode || numberToStepWord(step.sequence),
                                                    }))}
                                                value={(() => {
                                                    const pid = (editValues as any).parentId;
                                                    if (pid == null || pid === '') return null;
                                                    const step = steps.find((s) => s.id === pid);
                                                    return step ? { id: step.id, label: step.stepCode || numberToStepWord(step.sequence) } : null;
                                                })()}
                                                onChange={(_, newValue) => updateEditField('parentId' as any, newValue?.id ?? null)}
                                                getOptionLabel={(option) => (typeof option === 'object' && option?.label != null ? option.label : String(option))}
                                                isOptionEqualToValue={(option, value) => option.id === value?.id}
                                                size="small"
                                                renderInput={(params) => (
                                                    <TextField
                                                        {...params}
                                                        placeholder="None or select step..."
                                                        sx={{
                                                            '& .MuiOutlinedInput-root': { fontSize: '0.875rem', height: '32px' },
                                                        }}
                                                    />
                                                )}
                                                sx={{ width: '100%' }}
                                            />
                                        </Box>
                                        
                                        <Box>
                                            <Typography variant="body2" sx={{ mb: 0.5, fontSize: 12 }}>Sub Type</Typography>
                                            <SAPTextField 
                                                value={(editValues as any).subType || ''} 
                                                onChange={(e) => updateEditField('subType' as any, e.target.value)} 
                                                size="sm" 
                                                fullWidth 
                                                placeholder="e.g. SFTP (optional)"
                                            />
                                        </Box>
                                        
                                        {/* Row 4 */}
                                        <Box>
                                            <Typography variant="body2" sx={{ mb: 0.5, fontSize: 12 }}>Path</Typography>
                                            <SAPTextField 
                                                value={(editValues as any).path || ''} 
                                                onChange={(e) => updateEditField('path' as any, e.target.value)} 
                                                size="sm" 
                                                fullWidth 
                                                placeholder="e.g. /api/endpoint (optional)"
                                            />
                                        </Box>
                                        
                                        <Box>
                                            <Typography variant="body2" sx={{ mb: 0.5, fontSize: 12 }}>Object Type</Typography>
                                            <Autocomplete
                                                options={objectTypeOptions}
                                                value={(editValues as any).objectType || null}
                                                onChange={(_, newValue) => updateEditField('objectType' as any, (typeof newValue === 'string' ? newValue : newValue ?? '') || '')}
                                                freeSolo
                                                size="small"
                                                renderInput={(params) => (
                                                    <TextField
                                                        {...params}
                                                        placeholder="Select or type..."
                                                        sx={{
                                                            '& .MuiOutlinedInput-root': { fontSize: '0.875rem', height: '32px' },
                                                        }}
                                                    />
                                                )}
                                                sx={{ width: '100%' }}
                                            />
                                        </Box>
                                        
                                        {/* Row 5 */}
                                        <Box>
                                            <Typography variant="body2" sx={{ mb: 0.5, fontSize: 12 }}>Response Type</Typography>
                                            <SAPTextField 
                                                value={(editValues as any).responseType || ''} 
                                                onChange={(e) => updateEditField('responseType' as any, e.target.value)} 
                                                size="sm" 
                                                fullWidth 
                                                placeholder="e.g. JSON, XML (optional)"
                                            />
                                        </Box>
                                        
                                        <Box>
                                            <Typography variant="body2" sx={{ mb: 0.5, fontSize: 12 }}>Request Type</Typography>
                                            <SAPTextField 
                                                value={(editValues as any).requestType || ''} 
                                                onChange={(e) => updateEditField('requestType' as any, e.target.value)} 
                                                size="sm" 
                                                fullWidth 
                                            />
                                        </Box>
                                        
                                        {/* Row 6 */}
                                        <Box>
                                            <Typography variant="body2" sx={{ mb: 0.5, fontSize: 12 }}>Sequence</Typography>
                                            <SAPTextField 
                                                type="number" 
                                                value={(editValues as any).sequence ?? 0} 
                                                onChange={(e) => updateEditField('sequence' as any, Number(e.target.value))} 
                                                size="sm" 
                                                fullWidth 
                                                helperText={(validationErrors as any).sequence ? (validationErrors as any).sequence : ' '} 
                                                error={!!(validationErrors as any).sequence} 
                                            />
                                        </Box>
                                        
                                        <Box>
                                            <Typography variant="body2" sx={{ mb: 0.5, fontSize: 12 }}>Result</Typography>
                                            <Autocomplete
                                                options={resultOptions}
                                                value={(editValues as any).result || null}
                                                onChange={(_, newValue) => updateEditField('result' as any, (typeof newValue === 'string' ? newValue : newValue ?? '') || '')}
                                                freeSolo
                                                size="small"
                                                renderInput={(params) => (
                                                    <TextField
                                                        {...params}
                                                        placeholder="Select or type..."
                                                        sx={{
                                                            '& .MuiOutlinedInput-root': { fontSize: '0.875rem', height: '32px' },
                                                        }}
                                                    />
                                                )}
                                                sx={{ width: '100%' }}
                                            />
                                        </Box>
                                    </Box>
                                    
                                    {/* Additional fields below the grid */}
                                    <Box sx={{ 
                                        mt: 2, 
                                        display: 'grid', 
                                        gridTemplateColumns: '1fr 1fr', 
                                        gap: 2,
                                        '& .MuiTextField-root': { width: '100%' }
                                    }}>
                                        <Box>
                                            <Typography variant="body2" sx={{ mb: 0.5, fontSize: 12 }}>Success Key</Typography>
                                            <SAPTextField 
                                                value={(editValues as any).successKey || ''} 
                                                onChange={(e) => updateEditField('successKey' as any, e.target.value)} 
                                                size="sm" 
                                                fullWidth 
                                            />
                                        </Box>
                                        
                                        <Box>
                                            <Typography variant="body2" sx={{ mb: 0.5, fontSize: 12 }}>Success Value</Typography>
                                            <SAPTextField 
                                                value={(editValues as any).successValue || ''} 
                                                onChange={(e) => updateEditField('successValue' as any, e.target.value)} 
                                                size="sm" 
                                                fullWidth 
                                            />
                                        </Box>
                                    </Box>
                                    
                                    {/* Switches below the additional fields */}
                                    <Box sx={{ 
                                        mt: 2, 
                                        display: 'flex', 
                                        flexDirection: 'column',
                                        gap: 2
                                    }}>
                                        <FormControlLabel 
                                            control={<Switch checked={!!(editValues as any).loggingEnabled} onChange={(e) => updateEditField('loggingEnabled' as any, e.target.checked)} />} 
                                            label="Logging Enabled" 
                                            sx={{ margin: 0 }}
                                        />
                                        <FormControlLabel 
                                            control={<Switch checked={!!(editValues as any).beforeAgentCall} onChange={(e) => updateEditField('beforeAgentCall' as any, e.target.checked)} />} 
                                            label="Before Agent Call" 
                                            sx={{ margin: 0 }}
                                        />
                                        <Box>
                                            <FormControlLabel 
                                                control={
                                                    <Switch 
                                                        checked={!!(editValues as any).autoRetryEnabled || !!(editValues as any).automaticRetry} 
                                                        onChange={(e) => {
                                                            updateEditField('autoRetryEnabled' as any, e.target.checked);
                                                            if (!e.target.checked) {
                                                                updateEditField('maxAutoRetryCount' as any, null);
                                                            }
                                                        }} 
                                                    />
                                                } 
                                                label="Automatic Retry" 
                                                sx={{ margin: 0 }}
                                            />
                                            {((editValues as any).autoRetryEnabled || (editValues as any).automaticRetry) && (
                                                <Box sx={{ mt: 1, ml: 4 }}>
                                                    <Typography variant="body2" sx={{ mb: 0.5, fontSize: 12 }}>Max Auto Retry Count</Typography>
                                                    <SAPTextField 
                                                        type="number" 
                                                        value={(editValues as any).maxAutoRetryCount ?? ''} 
                                                        onChange={(e) => updateEditField('maxAutoRetryCount' as any, e.target.value ? Number(e.target.value) : null)} 
                                                        size="sm" 
                                                        fullWidth 
                                                        inputProps={{ min: 0 }}
                                                    />
                                                </Box>
                                            )}
                                        </Box>
                                    </Box>
                                </Box>
                                {/* RIGHT COLUMN - Contract & Code Section */}
                                <Box sx={{ gridColumn: '2 / 3' }}>
                                    <Typography variant="h6" sx={{ 
                                        fontSize: 16, // 16px section headers as per design spec
                                        fontWeight: SAP_TYPOGRAPHY.fontWeight.semibold,
                                        mb: 2,
                                        color: 'text.primary'
                                    }}>
                                        Contract & Code
                                    </Typography>

                                    {/* Collapsible: Header Template */}
                                    <Box sx={{ mb: 2 }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                                            <Typography variant="body2" sx={{ fontSize: 12 }}>Header Template</Typography>
                                            <IconButton size="small" onClick={() => setOpenEditors(s => ({ ...s, header: !s.header }))}>
                                                {openEditors.header ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                            </IconButton>
                                        </Box>
                                        <Collapse in={openEditors.header} timeout="auto" unmountOnExit>
                                            <SAPTextField 
                                                value={(editValues as any).headerTemplate || ''} 
                                                onChange={(e) => updateEditField('headerTemplate' as any, e.target.value)} 
                                                multiline 
                                                minRows={6} 
                                                maxRows={12} 
                                                size="sm" 
                                                InputProps={{ 
                                                    sx: { 
                                                        fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
                                                        fontSize: 12 // 12px labels as per design spec
                                                    } 
                                                }} 
                                            />
                                        </Collapse>
                                    </Box>

                                    {/* Collapsible: Request Template */}
                                    <Box sx={{ mb: 2 }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                                            <Typography variant="body2" sx={{ fontSize: 12 }}>Request Template</Typography>
                                            <IconButton size="small" onClick={() => setOpenEditors(s => ({ ...s, request: !s.request }))}>
                                                {openEditors.request ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                            </IconButton>
                                        </Box>
                                        <Collapse in={openEditors.request} timeout="auto" unmountOnExit>
                                            <SAPTextField 
                                                value={(editValues as any).requestTemplate || ''} 
                                                onChange={(e) => updateEditField('requestTemplate' as any, e.target.value)} 
                                                multiline 
                                                minRows={8} 
                                                maxRows={16} 
                                                size="sm" 
                                                InputProps={{ 
                                                    sx: { 
                                                        fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
                                                        fontSize: 12
                                                    } 
                                                }} 
                                            />
                                        </Collapse>
                                    </Box>

                                    {/* Collapsible: Request Formatter */}
                                    <Box sx={{ mb: 2 }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                                            <Typography variant="body2" sx={{ fontSize: 12 }}>Request Formatter (JS)</Typography>
                                            <IconButton size="small" onClick={() => setOpenEditors(s => ({ ...s, requestFmt: !s.requestFmt }))}>
                                                {openEditors.requestFmt ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                            </IconButton>
                                        </Box>
                                        <Collapse in={openEditors.requestFmt} timeout="auto" unmountOnExit>
                                            <SAPTextField 
                                                value={(editValues as any).requestFormatterJs || (editValues as any).requestFormatter || ''} 
                                                onChange={(e) => updateEditField('requestFormatterJs' as any, e.target.value)} 
                                                multiline 
                                                minRows={6} 
                                                maxRows={12} 
                                                size="sm" 
                                                placeholder="function formatRequest(request) { return request; }" 
                                                InputProps={{ 
                                                    sx: { 
                                                        fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
                                                        fontSize: 12
                                                    } 
                                                }} 
                                            />
                                        </Collapse>
                                    </Box>

                                    {/* Collapsible: Response Formatter */}
                                    <Box sx={{ mb: 2 }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                                            <Typography variant="body2" sx={{ fontSize: 12 }}>Response Formatter (JS)</Typography>
                                            <IconButton size="small" onClick={() => setOpenEditors(s => ({ ...s, responseFmt: !s.responseFmt }))}>
                                                {openEditors.responseFmt ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                            </IconButton>
                                        </Box>
                                        <Collapse in={openEditors.responseFmt} timeout="auto" unmountOnExit>
                                            <SAPTextField 
                                                value={(editValues as any).responseFormatterJs || (editValues as any).responseFormatter || ''} 
                                                onChange={(e) => updateEditField('responseFormatterJs' as any, e.target.value)} 
                                                multiline 
                                                minRows={6} 
                                                maxRows={12} 
                                                size="sm" 
                                                placeholder="function format(response) { return response; }" 
                                                InputProps={{ 
                                                    sx: { 
                                                        fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
                                                        fontSize: 12
                                                    } 
                                                }} 
                                            />
                                        </Collapse>
                                    </Box>

                                    {/* Collapsible: Error Formatter */}
                                    <Box sx={{ mb: 2 }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                                            <Typography variant="body2" sx={{ fontSize: 12 }}>Error Formatter (JS)</Typography>
                                            <IconButton size="small" onClick={() => setOpenEditors(s => ({ ...s, errorFmt: !s.errorFmt }))}>
                                                {openEditors.errorFmt ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                            </IconButton>
                                        </Box>
                                        <Collapse in={openEditors.errorFmt} timeout="auto" unmountOnExit>
                                            <SAPTextField 
                                                value={(editValues as any).errorFormatterJs || (editValues as any).errorFormatter || ''} 
                                                onChange={(e) => updateEditField('errorFormatterJs' as any, e.target.value)} 
                                                multiline 
                                                minRows={6} 
                                                maxRows={12} 
                                                size="sm" 
                                                placeholder="function formatError(error) { return error; }" 
                                                InputProps={{ 
                                                    sx: { 
                                                        fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
                                                        fontSize: 12
                                                    } 
                                                }} 
                                            />
                                        </Collapse>
                                    </Box>
                                </Box>
                            </Box>
                            </Box>
                        ) : (
							<Typography variant="body2" color="text.secondary">Select a step to edit.</Typography>
						)}
					</>
				)}

				{tab === 1 && selectedStep?.id && (
					<StepSettings stepId={selectedStep.id} />
				)}

				{tab === 2 && selectedStep?.id && (
					<StepRequestValidator stepId={selectedStep.id} />
				)}
			</CardContent>
		</Card>
	);
}

export default StepDetailTabs;


